'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculationResult {
  directSaleValue: number;
  auctionValue: number;
  savings: number;
  savingsPercentage: number;
  timeToSale: {
    directSale: number;
    auction: number;
  };
  totalCosts: {
    directSale: number;
    auction: number;
  };
}

const ROICalculator = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [fleetSize, setFleetSize] = useState<string>('5');
  const [avgVehicleValue, setAvgVehicleValue] = useState<string>('45000');
  const [result, setResult] = useState<CalculationResult | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const calculateROI = () => {
    const size = parseInt(fleetSize) || 0;
    const value = parseInt(avgVehicleValue) || 0;
    const totalFleetValue = size * value;

    // Direct Sale: 15-30% above market (using 22.5% average)
    const directSaleMultiplier = 1.225;
    const directSaleValue = totalFleetValue * directSaleMultiplier;
    const directSaleCosts = 0; // No fees

    // Auction: 10-20% below market (using 15% average) + fees
    const auctionMultiplier = 0.85;
    const auctionBaseValue = totalFleetValue * auctionMultiplier;
    const buyerFees = auctionBaseValue * 0.115; // 11.5% average buyer fees
    const sellerFees = auctionBaseValue * 0.055; // 5.5% average seller fees
    const auctionValue = auctionBaseValue - sellerFees;
    const auctionCosts = buyerFees + sellerFees;

    const savings = directSaleValue - auctionValue;
    const savingsPercentage = ((savings / auctionValue) * 100);

    setResult({
      directSaleValue,
      auctionValue,
      savings,
      savingsPercentage,
      timeToSale: {
        directSale: 10, // 7-14 days average
        auction: 45 // 30-60 days average
      },
      totalCosts: {
        directSale: directSaleCosts,
        auction: auctionCosts
      }
    });
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 md:py-24 bg-muted">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-card rounded-lg shadow-brand p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
              <div className="h-32 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-muted">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="CalculatorIcon" size={20} variant="outline" className="text-primary" />
            <span className="text-sm font-semibold text-primary">Interactive ROI Calculator</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-headline text-foreground mb-4">
            Calculate Your Potential Savings
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            See exactly how much more you could earn by choosing direct sale over traditional auction channels.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Input */}
          <div className="bg-card rounded-lg shadow-brand p-8">
            <h3 className="text-xl font-semibold font-headline text-foreground mb-6">
              Enter Your Fleet Details
            </h3>

            <div className="space-y-6">
              {/* Fleet Size Input */}
              <div>
                <label htmlFor="fleetSize" className="block text-sm font-medium text-foreground mb-2">
                  Number of Vehicles
                </label>
                <input
                  id="fleetSize"
                  type="number"
                  min="1"
                  max="1000"
                  value={fleetSize}
                  onChange={(e) => setFleetSize(e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                  placeholder="Enter number of vehicles"
                />
              </div>

              {/* Average Vehicle Value Input */}
              <div>
                <label htmlFor="avgValue" className="block text-sm font-medium text-foreground mb-2">
                  Average Vehicle Value ($)
                </label>
                <input
                  id="avgValue"
                  type="number"
                  min="1000"
                  max="500000"
                  step="1000"
                  value={avgVehicleValue}
                  onChange={(e) => setAvgVehicleValue(e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                  placeholder="Enter average vehicle value"
                />
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateROI}
                className="w-full px-6 py-4 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-0.5 shadow-brand flex items-center justify-center gap-2"
              >
                <Icon name="ChartBarIcon" size={20} variant="outline" />
                Calculate Savings
              </button>
            </div>

            {/* Assumptions */}
            <div className="mt-8 p-4 bg-muted rounded-md">
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="InformationCircleIcon" size={16} variant="outline" />
                Calculation Assumptions
              </h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Direct Sale: 22.5% above market value, zero fees</li>
                <li>• Auction: 15% below market value, 11.5% buyer fees, 5.5% seller fees</li>
                <li>• Based on industry averages from 2025-2026 data</li>
              </ul>
            </div>
          </div>

          {/* Results Display */}
          <div className="bg-card rounded-lg shadow-brand p-8">
            {result ? (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold font-headline text-foreground mb-6">
                  Your Potential Savings
                </h3>

                {/* Main Savings Highlight */}
                <div className="bg-gradient-to-br from-success/10 to-success/5 border-2 border-success rounded-lg p-6 text-center">
                  <div className="text-sm font-medium text-success mb-2">Total Additional Value</div>
                  <div className="text-4xl font-bold text-success mb-2">
                    {formatCurrency(result.savings)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {result.savingsPercentage.toFixed(1)}% more than auction
                  </div>
                </div>

                {/* Detailed Breakdown */}
                <div className="space-y-4">
                  {/* Direct Sale Value */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-md">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                        <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">Direct Sale Value</div>
                        <div className="text-xs text-muted-foreground">No fees or commissions</div>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-success">
                      {formatCurrency(result.directSaleValue)}
                    </div>
                  </div>

                  {/* Auction Value */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-md">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
                        <Icon name="XCircleIcon" size={20} variant="solid" className="text-error" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">Auction Value</div>
                        <div className="text-xs text-muted-foreground">
                          After {formatCurrency(result.totalCosts.auction)} in fees
                        </div>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-error">
                      {formatCurrency(result.auctionValue)}
                    </div>
                  </div>

                  {/* Time Comparison */}
                  <div className="flex items-center justify-between p-4 bg-muted rounded-md">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="ClockIcon" size={20} variant="outline" className="text-primary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">Time Savings</div>
                        <div className="text-xs text-muted-foreground">Faster transaction</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-success">
                        {result.timeToSale.directSale} days
                      </div>
                      <div className="text-xs text-muted-foreground line-through">
                        {result.timeToSale.auction} days
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <button className="w-full px-6 py-4 bg-cta text-cta-foreground font-semibold rounded-md hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-0.5 shadow-brand">
                  Get Your Actual Fleet Valuation
                </button>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name="CalculatorIcon" size={32} variant="outline" className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Ready to Calculate?
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Enter your fleet details on the left to see your potential savings with direct sale.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;